import React, { PropsWithChildren } from 'react';
import Box from '@mui/material/Box';

import { GatsbyLinkProps } from 'gatsby';
import { SSR } from '~/common';
import { veryLightGrey } from '~/styles';

import {
  Card,
  CardMedia,
  Content,
  FooterWrapper,
  ImageWrapper,
  Link,
  UpperLeft,
  UpperRight,
} from './elements';
import { Divider } from '../../Divider';
import { LazyLoadWrapper } from '../../LazyLoadWrapper';

export interface Dimension {
  height: number;
  width: number;
  maxWidth?: string;
  maxHeight?: string;
}

export interface BaseCardProps {
  dimension: Dimension;
  noShadow?: boolean;
  hoverable?: boolean | undefined;
  url: string;
  thumb: string;
  thumbLabel?: string;
  imageHeight: number;
  bgImageFull: boolean;
  lazyLoad?: boolean;
  footerStyles?: React.CSSProperties;
  cardStyles?: React.CSSProperties;
  cardMediaStyles?: React.CSSProperties;
  onClick?(e: React.MouseEvent<HTMLElement>): void;
  renderImageOverlay?(): React.ReactNode;
  renderContent?(): React.ReactNode;
  renderUpperLeft?(): React.ReactNode;
  renderUpperRight?(): React.ReactNode;
  renderLowerLeft?(): React.ReactNode;
  renderLowerRight?(): React.ReactNode;
  renderFooter?(): React.ReactNode;
  renderCardOverlay?(): React.ReactNode;
  renderProgressBarInMedia?(): React.ReactNode;
  renderProgressBarInFooter?(): React.ReactNode;
  testId?: string;
  linkProps?: Partial<GatsbyLinkProps<unknown>>;
}

export const BaseCard: React.FC<PropsWithChildren<BaseCardProps>> = ({
  dimension,
  noShadow,
  hoverable = false,
  url,
  thumb,
  thumbLabel = '',
  imageHeight,
  bgImageFull,
  lazyLoad,
  footerStyles = {},
  cardStyles = {},
  cardMediaStyles = {},
  onClick = () => undefined,
  renderImageOverlay = () => undefined,
  renderContent = () => undefined,
  renderUpperLeft = () => undefined,
  renderUpperRight = () => undefined,
  renderLowerLeft = () => undefined,
  renderLowerRight = () => undefined,
  renderProgressBarInMedia = () => undefined,
  renderProgressBarInFooter = () => undefined,
  renderCardOverlay = () => undefined,
  renderFooter,
  testId,
  linkProps,
}) => {
  const hoveredDimension = {
    height: dimension.height + (dimension.height * 20) / 100,
    width: dimension.width + (dimension.width * 20) / 100,
  };

  const hover = hoverable ? 1 : 0;
  const displayedOnTestPage =
    !SSR && window.location && window.location.pathname.includes('/visual-test');

  // work around for https://github.com/styled-components/styled-components/issues/1198
  return (
    <Card
      dimension={dimension}
      noShadow={noShadow}
      hoveredDimension={hoveredDimension}
      hoverable={hover}
      data-test-percy-hidden={!displayedOnTestPage}
      data-testid="base-card"
      style={cardStyles}
    >
      <Link
        data-testid={testId}
        to={url}
        onClick={onClick}
        aria-label="main card url"
        {...linkProps}
      >
        <LazyLoadWrapper enabled={lazyLoad} height={bgImageFull ? '100%' : imageHeight}>
          <ImageWrapper style={cardMediaStyles} imageHeight={imageHeight} bgImageFull={bgImageFull}>
            <CardMedia
              image={thumb}
              imageHeight={imageHeight}
              bgImageFull={bgImageFull}
              aria-label={thumbLabel || 'card main image'}
            >
              {renderImageOverlay()}
              {renderProgressBarInMedia()}
            </CardMedia>
          </ImageWrapper>
        </LazyLoadWrapper>
        <Content>{renderContent()}</Content>
        <UpperLeft>{renderUpperLeft()}</UpperLeft>
        <UpperRight>{renderUpperRight()}</UpperRight>
        <FooterWrapper style={footerStyles}>
          {renderLowerLeft()}
          {renderLowerRight()}
        </FooterWrapper>
      </Link>
      {renderCardOverlay()}
      {renderProgressBarInFooter()}
      {renderFooter && (
        <Box style={{ position: 'absolute', bottom: '6px', width: '100%', zIndex: '25' }}>
          <Divider style={{ marginTop: '10px', padding: '0 15px' }} color={veryLightGrey} />
          {renderFooter()}
        </Box>
      )}
    </Card>
  );
};
